import { mutatePartners } from "@/api/partner/usePartnerQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import styled from "@emotion/styled";
import { Box, Button, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { MasterApiPartnersPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface PartnerCreateFormProps {
  code?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}
export const PartnerCreateForm = (params: PartnerCreateFormProps) => {
  const { code, autoCompleteOnSubmit } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { mutate: postMutate } = useMutation(
    (params: MasterApiPartnersPostRequest) =>
      mutatePartners.create(params).mutationFn(params as MasterApiPartnersPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['partners']);
      },
    }
  );

  const form = useForm({
    initialValues: {
      code: code ?? undefined,
      name: undefined,
      contact: undefined,
      type: undefined,
    },
  });

  const onSubmit = async () => {
    try {
      postMutate(
        {
          partnersGetRequest: {
            code: code ?? form.values.code!,
            name: form.values.name!,
            contact: form.values.contact!,
            type: form.values.type!,
          }
        },
        {
          onSuccess: (res) => {
            closeModal(form.values);
            if (res.status === 200) {
              customAlert(
                "협력사 생성에 성공하였습니다.",
                "생성 성공",
                "green"
              )
            } else {
              customAlert(
                "협력사 생성에 실패하였습니다.",
                "생성 실패",
                "red"
              )
            }
          },
          onError: (error: any) => {
            customAlert(handleErrorResponse({ error, errorContext: '협력사' }), '협력사 생성 실패', 'red')
          }
        },

      )
    } catch (e) {
      customAlert(
        "협력사 생성에 실패하였습니다.",
        "생성 실패",
        "red"
      )
    }
  };

  return (
    <Box w={320} mx="auto">
      <Form>
        <ModalFieldSet>
          <TextInput
            withAsterisk
            label="협력사 코드"
            {...form.getInputProps("code")}
          />
          <TextInput
            withAsterisk
            label="협력사 명"
            {...form.getInputProps("name")}
          />
          <TextInput
            label="연락처"
            withAsterisk
            {...form.getInputProps("contact")}
          />
          <Select
            withAsterisk
            label="협력사 타입"
            {...form.getInputProps("type")}
            data={[
              { value: "SALES", label: "판매" },
              { value: "PURCHASE", label: "구매" },
              { value: "BI_DIRECTION", label: "양방향" },
            ]}
          />
          <ModalConfirmBtnBox>
            <Button color="gray" onClick={closeModal}>
              취소
            </Button>
            <Button
              disabled={validateFormValues({
                pageType: "partner",
                values: form.values,
              })}
              onClick={autoCompleteOnSubmit ? () => autoCompleteOnSubmit(form.values) : onSubmit}
            >
              협력사 생성
            </Button>
          </ModalConfirmBtnBox>
        </ModalFieldSet>
      </Form>
    </Box>
  );
};

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const ModalFieldSet = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalConfirmBtnBox = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
`;
